import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from "react";
import { cookieExist } from "./util";
import { AEA_PLUGIN_PRESENT_COOKIE_NAME, AWSM_VALID_SESSION_COOKIE_NAME, InteractableElements, ProfileUrls, } from "./constants";
import NavatarLoader from "./NavatarLoader";
import { UserAvatar } from "./avatar";
import { SignInType, useAuth, } from "@amzn/aws-marketing-identity-kit-essentials";
import { useLocalizedString } from "@amzn/aws-marketing-identity-kit-localizations";
import FederateLoginModal from "./components/FederateLoginModal";
export const NavatarBase = ({ isProd, locale, theme, labels, onPopoverOpen, onElementClick, displayGlobalDialog, }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(cookieExist(AWSM_VALID_SESSION_COOKIE_NAME));
    const [authUpdated, setAuthUpdated] = useState(false);
    const [isActive, setIsActive] = useState(false);
    const [currentHref, setCurrentHref] = useState("");
    const [avatarUrl, setAvatarUrl] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    const [, setError] = useState(null);
    const containerRef = useRef(null);
    const [enableFederateLoginModal, setEnableFederateLoginModal] = useState(false);
    const popoverRef = useRef(null);
    const [transitionStatus, setTransitionStatus] = useState("close");
    const { login, logout, setSession, loadProfile, profile } = useAuth();
    const { t, i18n } = useLocalizedString();
    const aeaPluginPresent = cookieExist(AEA_PLUGIN_PRESENT_COOKIE_NAME);
    const getEncodedUrl = () => {
        return encodeURIComponent(currentHref);
    };
    const togglePopover = () => {
        if (isActive) {
            setTransitionStatus("close");
            setTimeout(() => {
                setIsActive(false);
            }, 100);
        }
        else {
            setIsActive(true);
            setTransitionStatus("open");
            if (onPopoverOpen) {
                onPopoverOpen();
            }
        }
    };
    const handleOnElementClick = (key) => {
        if (onElementClick) {
            onElementClick(key);
        }
    };
    useEffect(() => {
        if (locale) {
            i18n.changeLanguage(locale);
        }
    }, [locale, i18n]);
    useEffect(() => {
        setCurrentHref(window.location.href);
    }, []);
    useEffect(() => {
        if (profile?.picture) {
            setAvatarUrl(profile?.picture);
        }
    }, [profile]);
    useEffect(() => {
        setAuthUpdated(true);
        if (isAuthenticated) {
            const setBpSession = async () => {
                try {
                    await setSession();
                    await loadProfile();
                }
                catch (e) {
                    setError(e);
                    setIsAuthenticated(false);
                }
                finally {
                    setIsLoading(false);
                }
            };
            setBpSession();
        }
    }, [isAuthenticated]);
    useEffect(() => {
        if (authUpdated) {
            setIsAuthenticated(cookieExist(AWSM_VALID_SESSION_COOKIE_NAME));
        }
    }, [authUpdated]);
    useEffect(() => {
        const value = !!localStorage.getItem("enableFederateLoginModal");
        setEnableFederateLoginModal(value);
    }, []);
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isActive &&
                containerRef.current &&
                !containerRef.current.contains(event.target)) {
                setTransitionStatus("close");
                setTimeout(() => {
                    setIsActive(false);
                }, 100);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isActive]);
    useEffect(() => {
        const handleEscapeKey = (event) => {
            if (isActive && event.key === "Escape") {
                setTransitionStatus("close");
                setTimeout(() => {
                    setIsActive(false);
                }, 100);
            }
        };
        document.addEventListener("keydown", handleEscapeKey);
        return () => {
            document.removeEventListener("keydown", handleEscapeKey);
        };
    }, [isActive]);
    return (_jsxs("div", { ref: containerRef, className: `awsm-ik-navatar awsm-ik-navatar-${theme}`, children: [_jsx("button", { onClick: togglePopover, className: "awsm-ik-navatar-icon", "aria-label": "navatar-icon", children: authUpdated ? (isAuthenticated ? (isLoading ? (_jsx(NavatarLoader, {})) : (_jsx(UserAvatar, { src: avatarUrl, name: profile?.name || "" }))) : (_jsx("i", { className: "icon-user-o-aura" }))) : (_jsx(NavatarLoader, {})) }), isActive && (_jsxs("div", { className: "awsm-ik-navatar-popover popover-scale", ref: popoverRef, "aria-label": "navatar-popover", "data-transition-status": transitionStatus, "data-placement": "bottom", children: [_jsx("div", { className: "close-button", onClick: () => {
                            setTransitionStatus("close");
                            setTimeout(() => {
                                setIsActive(false);
                            }, 100);
                        }, onKeyDown: (e) => {
                            if (e.key === "Enter") {
                                setTransitionStatus("close");
                                setTimeout(() => {
                                    setIsActive(false);
                                }, 100);
                            }
                        }, "aria-label": "close-button", tabIndex: 0, role: "button", children: _jsx("i", { className: "close-icon" }) }), " ", isAuthenticated ? (_jsxs("div", { className: "awsm-ik-navatar-popover__auth-view", children: [_jsxs("div", { className: "header", children: [_jsx(UserAvatar, { src: avatarUrl, name: profile?.name || "" }), _jsxs("div", { className: "profile-info", children: [_jsx("span", { "data-testid": "navatarProfileName", className: "awsm-ik__title-4 awsm-ik-navatar-popover__text", children: profile?.name }), _jsxs("span", { "data-testid": "navatarProfileAlias", className: "profile-alias awsm-ik-navatar-popover__text", children: [" ", profile?.alias && "@" + profile?.alias] })] })] }), _jsxs("div", { className: "buttons", children: [_jsx("div", { "data-testid": "navatarLogoutButton", className: "awsm-ik-navatar-popover__secondary-button", onClick: () => {
                                            handleOnElementClick(InteractableElements.LOG_OUT);
                                            logout(getEncodedUrl());
                                        }, onKeyDown: (e) => {
                                            if (e.key === "Enter") {
                                                handleOnElementClick(InteractableElements.LOG_OUT);
                                                logout(getEncodedUrl());
                                            }
                                        }, role: "button", tabIndex: 0, children: _jsx("p", { className: "awsm-ik-navatar-popover__text awsm-ik-navatar-popover__subheader", children: labels?.logOutText || t("Navatar.logOutText") }) }), _jsx("div", { className: "awsm-ik-navatar-popover__primary-button", onClick: () => {
                                            handleOnElementClick(InteractableElements.PROFILE);
                                        }, onKeyDown: (e) => {
                                            if (e.key === "Enter") {
                                                handleOnElementClick(InteractableElements.PROFILE);
                                            }
                                        }, role: "button", tabIndex: 0, children: _jsx("a", { href: isProd ? ProfileUrls.PROD : ProfileUrls.GAMMA, className: "awsm-ik-navatar-popover__profile-link awsm-ik-navatar-popover__interactive", tabIndex: -1, children: _jsx("p", { className: "awsm-ik-navatar-popover__text awsm-ik-navatar-popover__subheader", children: labels?.profileButtonText ||
                                                    t("Navatar.profileButtonText") }) }) })] })] })) : (_jsxs("div", { children: [_jsx("div", { className: "awsm-ik-navatar-popover__header awsm-ik-navatar-popover__text", children: labels?.header || t("Navatar.header") }), _jsx("div", { className: "awsm-ik-navatar-popover__subheader awsm-ik-navatar-popover__text", children: labels?.subheader || t("Navatar.subheader") }), _jsxs("div", { className: "buttons", children: [_jsx("div", { "data-testid": "navatarLogInButton", className: "awsm-ik-navatar-popover__secondary-button", onClick: () => {
                                            handleOnElementClick(InteractableElements.LOG_IN);
                                            login(getEncodedUrl(), aeaPluginPresent
                                                ? SignInType.Federate
                                                : SignInType.BuilderId);
                                        }, onKeyDown: (e) => {
                                            if (e.key === "Enter") {
                                                handleOnElementClick(InteractableElements.LOG_IN);
                                                login(getEncodedUrl(), aeaPluginPresent
                                                    ? SignInType.Federate
                                                    : SignInType.BuilderId);
                                            }
                                        }, role: "button", tabIndex: 0, children: _jsx("p", { className: "awsm-ik-navatar-popover__text awsm-ik-navatar-popover__subheader", children: labels?.logInText || t("Navatar.logInText") }) }), _jsx("div", { "data-testid": "navatarCreateProfileButton", className: "awsm-ik-navatar-popover__primary-button", onClick: () => {
                                            handleOnElementClick(InteractableElements.CREAETE_PROFILE);
                                        }, onKeyDown: (e) => {
                                            if (e.key === "Enter") {
                                                handleOnElementClick(InteractableElements.CREAETE_PROFILE);
                                            }
                                        }, role: "button", tabIndex: 0, children: _jsx("a", { href: isProd ? ProfileUrls.PROD : ProfileUrls.GAMMA, className: "awsm-ik-navatar-popover__profile-link awsm-ik-navatar-popover__interactive", tabIndex: -1, children: _jsx("p", { className: "awsm-ik-navatar-popover__text awsm-ik-navatar-popover__subheader", children: labels?.createProfileText ||
                                                    t("Navatar.createProfileText") }) }) })] })] }))] })), enableFederateLoginModal &&
                displayGlobalDialog &&
                containerRef.current &&
                isAuthenticated &&
                !isLoading && (_jsx(FederateLoginModal, { profile: profile, labels: labels, handleOnElementClick: handleOnElementClick, getEncodedUrl: getEncodedUrl, login: login, theme: theme, container: containerRef.current, useContainer: true }))] }));
};
export default NavatarBase;
//# sourceMappingURL=NavatarBase.js.map